import React from 'react';
import { useMessages } from '../i18n/I18n';

/** How the content being loaded is laid out, so its placeholder takes the same shape */
export type PlaceholderLayout = 'rows' | 'table' | 'cards' | 'form';

interface SkeletonProps {
  width?: number | string;
  height?: number;
  radius?: number | string;
  className?: string;
}

/** A shimmering block standing in for one piece of content that is loading */
export const Skeleton: React.FC<SkeletonProps> = ({ width = '100%', height = 14, radius, className }) => (
  <span
    className={className ? `skeleton ${className}` : 'skeleton'}
    style={{ width, height, borderRadius: radius }}
    aria-hidden="true"
  />
);

// Varied line lengths, so the placeholder reads as text rather than a grid of bars
const WIDTHS = ['62%', '48%', '71%', '55%', '40%', '66%'];

const PlaceholderItem: React.FC<{ layout: PlaceholderLayout; index: number }> = ({ layout, index }) => {
  const width = WIDTHS[index % WIDTHS.length];
  switch (layout) {
    case 'table':
      return (
        <div className="placeholder-item">
          <Skeleton width="22%" />
          <Skeleton width="18%" />
          <Skeleton width={width === '40%' ? '30%' : '24%'} />
          <Skeleton width="12%" />
        </div>
      );
    case 'cards':
      return (
        <div className="placeholder-item">
          <Skeleton width={56} height={56} radius="var(--radius)" />
          <div className="placeholder-lines">
            <Skeleton width={width} height={16} />
            <Skeleton width="85%" height={12} />
            <Skeleton width="35%" height={12} />
          </div>
        </div>
      );
    case 'form':
      return (
        <div className="placeholder-item">
          <Skeleton width="28%" height={12} />
          <Skeleton height={36} radius="var(--radius)" />
          <Skeleton width={width} height={11} />
        </div>
      );
    case 'rows':
    default:
      return (
        <div className="placeholder-item">
          <Skeleton width={40} height={40} radius="var(--radius)" />
          <div className="placeholder-lines">
            <Skeleton width={width} />
            <Skeleton width="28%" height={11} />
          </div>
        </div>
      );
  }
};

/** Placeholder items without their frame, for screens that lay out their own placeholder */
export const PlaceholderItems: React.FC<{ layout: PlaceholderLayout; count: number }> = ({ layout, count }) => (
  <>
    {Array.from({ length: count }, (_, index) => <PlaceholderItem key={index} layout={layout} index={index} />)}
  </>
);

interface LoadingPlaceholderProps {
  layout?: PlaceholderLayout;
  count?: number;
}

/**
 * Stands in for a list or form while it loads, in its shape. It only appears after a short delay (CSS), so a fast load
 * never flashes, and it says "Loading..." to screen readers.
 */
const LoadingPlaceholder: React.FC<LoadingPlaceholderProps> = ({ layout = 'rows', count = 5 }) => {
  const common = useMessages('common');
  return (
    <div className={`loading-placeholder loading-placeholder-${layout}`} role="status" aria-busy="true">
      <span className="visually-hidden">{common.loading}</span>
      <PlaceholderItems layout={layout} count={count} />
    </div>
  );
};

export default LoadingPlaceholder;
